import styled from "styled-components";

const textPrimary = "rgba(232, 240, 252, 0.94)";
const textMuted = "rgba(178, 196, 224, 0.82)";
const textHeading = "#eef3ff";

export const AboutPageRoot = styled.div`
  flex: 1;
  min-height: 100%;
  width: 100%;
  color: ${textPrimary};
  background-color: #05060c;
  background-image:
    linear-gradient(
      165deg,
      rgba(127, 90, 240, 0.12) 0%,
      transparent 42%
    ),
    linear-gradient(
      215deg,
      rgba(13, 194, 254, 0.08) 0%,
      transparent 38%
    ),
    repeating-linear-gradient(
      -12deg,
      transparent,
      transparent 100px,
      rgba(255, 255, 255, 0.015) 100px,
      rgba(255, 255, 255, 0.015) 101px
    );
  isolation: isolate;
`;

/** Portrait + biography grid (md+); stacked on small screens. Horizontal alignment comes from parent `.container` (same as header). */
export const AboutPageLayout = styled.div`
  width: 100%;
  padding: clamp(1rem, 2.5vw, 1.75rem) 0 3.5rem;
  display: grid;
  gap: 1.75rem;
  align-items: start;

  @media (min-width: 768px) {
    grid-template-columns: minmax(260px, 380px) minmax(0, 1fr);
    gap: 2rem 2.5rem;
    padding: clamp(1.25rem, 3vw, 2.25rem) 0 4rem;
  }
`;

export const AboutPortraitColumn = styled.aside`
  width: 100%;
  justify-self: center;

  @media (min-width: 768px) {
    justify-self: stretch;
    position: sticky;
    top: 80px;
  }
`;

export const AboutPortraitFrame = styled.div`
  position: relative;
  width: 100%;
  max-width: 340px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: none;
    margin: 0;
  }

  &::before {
    content: "";
    position: absolute;
    inset: -6px;
    border-radius: 20px;
    background: #000000;
    z-index: 0;
    opacity: 1;
  }

  img {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: auto;
    border-radius: 16px;
    object-fit: cover;
    aspect-ratio: 2 / 3;
    min-height: clamp(300px, 52vh, 640px);
    background: #000000;
    box-shadow: 0 28px 64px rgba(0, 0, 0, 0.55);
  }
`;

export const AboutContent = styled.div`
  min-width: 0;
`;

export const AboutBioBlock = styled.section`
  display: grid;
  gap: 1rem;
  padding-left: 1rem;
  border-left: 3px solid transparent;
  border-image: linear-gradient(180deg, #9b7cff, #4dd4ff, rgba(255, 106, 0, 0.5))
    1;
  margin-bottom: 2.5rem;

  @media (min-width: 768px) {
    padding-left: 1.35rem;
    margin-bottom: 3rem;
  }
`;

export const AboutSectionHeading = styled.h2`
  margin: 0;
  font-size: clamp(0.72rem, 1.5vw, 0.8rem);
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${textMuted};
`;

export const AboutProse = styled.p`
  margin: 0 !important;
  font-size: 15px;
  line-height: 1.82;
  color: ${textPrimary} !important;
  max-width: 65ch;

  @media (min-width: 768px) {
    font-size: 17px;
  }
`;

export const AboutDualTrack = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 2.5rem;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
    gap: 0;
    margin-bottom: 3rem;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    overflow: hidden;
    background: rgba(255, 255, 255, 0.02);
  }
`;

export const AboutTrack = styled.div`
  @media (min-width: 992px) {
    padding: 1.5rem 1.65rem 1.75rem;

    &:first-of-type {
      border-right: 1px solid rgba(255, 255, 255, 0.08);
    }
  }

  @media (max-width: 991px) {
    padding: 1.35rem 1.25rem;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.03);
  }
`;

export const AboutTrackHeader = styled.div`
  margin-bottom: 1rem;
`;

export const AboutTrackTitle = styled.h3`
  margin: 0;
  font-size: clamp(0.72rem, 1.5vw, 0.8rem);
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${textMuted};
`;

export const AboutTimelineWrap = styled.div`
  .resume {
    box-shadow: none !important;
    color: ${textPrimary} !important;
  }

  .resume .func .work ul li {
    color: ${textPrimary} !important;
  }

  .resume .func .work ul li::before {
    border-color: rgba(160, 190, 235, 0.45) !important;
    background: linear-gradient(135deg, #8f6fff, #3ec8ff) !important;
  }

  .resume .func .work ul li::after {
    background: rgba(255, 255, 255, 0.1) !important;
  }

  .resume .func .work ul li:hover span {
    color: #c8ddff !important;
  }

  .resume .func .work ul li span {
    color: ${textHeading};
    font-weight: 600;
    font-size: 15px;
  }

  .resume .func .work small {
    color: ${textMuted} !important;
    opacity: 1 !important;
    font-size: 13px;
    margin-top: 4px;
  }
`;

export const AboutSkillsSection = styled.section`
  margin-bottom: 2.5rem;

  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

export const AboutSkillsHeader = styled.div`
  margin-bottom: 1.25rem;
`;

/** Self-contained chips — do not use global SkillSetContainer (white cards + black text). */
export const AboutSkillsFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.45rem;
`;

export const AboutSkillChip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 92px;
  max-width: 120px;
  flex: 1 1 92px;
  padding: 0.55rem 0.45rem 0.5rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.09);
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  &:hover {
    background: rgba(127, 90, 240, 0.12);
    border-color: rgba(159, 130, 255, 0.35);
  }

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    display: block;
  }
`;

export const AboutSkillLabel = styled.span`
  display: block;
  margin-top: 0.4rem;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: center;
  line-height: 1.25;
  color: ${textPrimary} !important;
`;

export const AboutHonorsSection = styled.section``;

export const AboutHonorsHeading = styled.h2`
  margin: 0 0 1.1rem;
  font-size: clamp(0.72rem, 1.5vw, 0.8rem);
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${textMuted};
`;

/** Single compact panel — avoids global li { color: #323232 } from index.css */
export const AboutHonorsPanel = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #0c0e16 !important;
  background-image: linear-gradient(
    105deg,
    rgba(127, 90, 240, 0.12) 0%,
    rgba(10, 12, 20, 0.98) 45%
  ) !important;
  border-left: 3px solid #9b7cff;
  padding: 1rem 1.15rem 1.05rem;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);

  @media (min-width: 768px) {
    padding: 1.15rem 1.35rem 1.2rem;
  }
`;

export const AboutHonorLine = styled.p`
  margin: 0 0 0.9rem 0 !important;
  font-size: 15px;
  line-height: 1.72;
  color: ${textPrimary} !important;
  background: transparent !important;

  &:last-of-type {
    margin-bottom: 0 !important;
  }

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

export const AboutHonorDivider = styled.hr`
  margin: 0.85rem 0;
  border: none;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;
